"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveIntentEntityCode = resolveIntentEntityCode;
/**
 * Resolves the entity code that should be stored on a newly-created upload
 * intent, enforcing the legacy vs unified loanBookMode policy consistently
 * across every upload path.
 *
 * Policy
 * ------
 * - `loanBookMode === 'unified'` (Timms, Intuitive Finance, etc.)
 *   → entity code is the canonical data dimension; written through to
 *   `raw_commissions.entity_code` and used in dedup + rollup queries.
 *   Returns the detected code.
 *
 * - `loanBookMode !== 'unified'` (Entourage and any other legacy tenant)
 *   → entity code MUST remain NULL in `raw_commissions` so the existing
 *   250K+ rows' dedup invariant is preserved. Filename-level entity codes
 *   are still used by the admin ZIP/MERU paths to route files to offices
 *   via the `entity_mappings` subcollection, but the intent itself does
 *   not carry the code downstream. Returns `null`.
 *
 * Call sites that MUST use this helper
 * ------------------------------------
 * - `services/client-functions/src/pipeline/0_ingress/issueSignedUploadUrl.ts`
 *   (client UI uploads)
 * - `services/admin-functions/src/uploads/processAdminZipUpload.ts`
 *   (admin LM-entity ZIP uploads)
 * - `services/admin-functions/src/uploads/processAdminMultiEntityUpload.ts`
 *   (admin MERU bulk uploads)
 *
 * `queueFileUpload` enforces the same rule as defense-in-depth by stripping
 * `intent.entityCode` for legacy-but-entity-aware companies and rejecting
 * intents for companies with no `entity_mappings` at all.
 */
async function resolveIntentEntityCode(db, companyId, detectedEntityCode) {
    if (!detectedEntityCode)
        return null;
    const companyDoc = await db.collection('companies').doc(companyId).get();
    const loanBookMode = companyDoc.data()?.loanBookMode || 'legacy';
    return loanBookMode === 'unified' ? detectedEntityCode : null;
}
